import React, { useState, useContext, useCallback, useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IState} from './types';
import * as S from './styles';
import { Context } from "routes/private/Editor/Context";
import { CloseIcon } from '@assets/icons/closeIcon';
import Loading from '@components/Loading';
import actions from '@state/actions';
import _ from "lodash";
import { IBrandImage, IType } from '@state/brandsImages/types';
import ClickOutside from '@components/ClickOutside';
import { brandSearchResults, getSearchResultGroup, getBrandsINquery } from '@utils/brand';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { IDSConfig } from '@state/dsconfig/types';

const SearchBrandLibrary:React.FC<IProps> = (props) => {
  const [fetchingSearch, setFetchingSearch]= useState(false);
  const [showMenu, setShowMenu]= useState(false);
  const [showLoading, setShowLoading]= useState(false);
  const [showClear, setShowClear]= useState(false);
  const [value, setValue] = useState('');
  const [searchList, setSearchList] = useState<any[]>([]);
  const [selectedSearchGroup, setSelectedSearchGroup] = useState<any|undefined>(undefined);

  const searchResults: IBrandImage[] = useSelector(selectors.brandsImages.getBrandImagesSearch);
  const searchResultscounter: any = useSelector(selectors.brandsImages.getCounterBrandImages);

  const authContext = useContext(AuthContext);
  const userData = authContext && authContext.userData?authContext.userData.profile:undefined;
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);

  let {onShowPicker, editorStatus} = useContext(Context);
  const dispatch = useDispatch();

  const debounce  = useCallback(
    _.debounce((_searchVal: string) => {
      const run = async () => {
        await fetchImages(_searchVal);
      }
      run();	
    }, 400),
    []
  );

  const fetchImages = (value: string) => {
    let brandsQuery: string = '';
    if (userData.role==='DealerUser') {
      brandsQuery = ' and ' + getBrandsINquery(dsConfig.brands??[]);
    }
    else brandsQuery = ' and c.organization=null and NOT IS_DEFINED(c.organization)';

    let query = `c.status='ACTIVE' and udf.searchMetadataBrandImage("${value}", c)${brandsQuery}&orderby=c.updateDate DESC`;

    return new Promise((resolve, reject) => {      
      dispatch(actions.brandsImages.fetchBrandImages(query, 0, 20, 'search',  (result, error) => {      
        if (error) reject(error)
        else {       
          resolve(result)}
      }))
    });
  }

  const fetchCounter = () => {
    let query = `?filter=c.status='ACTIVE' and udf.searchMetadataBrandImage("${value}", c)`;
    return new Promise((resolve, reject) => {      
      dispatch(actions.brandsImages.fetchCounterBrandImages(query, undefined, (result, error) => {      
        if (error) reject(error)
        else {       
          resolve(result)}
      }))
    });
  }

  useEffect(() => {
    if((searchResults)&&(value!=='')){
      setSearchList(brandSearchResults(searchResults, value));
      setShowLoading(false);
      if(value.length>3)setFetchingSearch(false);
    }
    else{
      setShowMenu(false);
      setShowLoading(false);
    }
  }, [searchResults]);

  const handleChange = (event: any) => {
    setValue(event.target.value);

    if(event.target.value.length>0)setShowClear(true);
    else handleClear();
    
    if(event.target.value.length>3){
      setFetchingSearch(true);
      setShowLoading(true);
      setShowMenu(true);
      debounce(event.target.value);
    }
    
    if(event.target.value.length==0)setShowMenu(false);
  };

  const handleKeyUp = async (e:any)=>{
    if (e.keyCode === 13) {
      setSelectedSearchGroup(undefined);
      if(searchResults?.length>0 && value.length>3){
        onShowPicker(editorStatus.currentPicker, {search: value});
        if (searchResults.length>0 && !fetchingSearch){
          setShowMenu(false);
        }
        if (props.onSelect) props.onSelect(undefined)
        if (props.category) dispatch(actions.brandsImages.setBrandImagesFilters(undefined, props.category ));
        await fetchCounter();
      }
    }
  }

  const handleClear = () => {
    setShowMenu(false);
    setValue('');
    setShowClear(false);
    onShowPicker(editorStatus.currentPicker, undefined);
    dispatch(actions.brandsImages.setCounterBrandImages(undefined, '' ));
    if (props.onSelect) props.onSelect(undefined)
    if (props.category) dispatch(actions.brandsImages.setBrandImagesFilters(undefined, props.category ));
  }

  const handleSelectItemSearch = (searchValue: any) => {
    if (props.onSelect){
      let resultByGroup: IBrandImage[] = getSearchResultGroup(searchResults, searchValue);
      setSelectedSearchGroup({search:searchValue, counter: resultByGroup?.length});
      props.onSelect(resultByGroup);
    } 
    setShowMenu(false);
  }

  const handleFocus = () => {
    if (searchResults?.length>0) setShowMenu(true);
  }

  const formatMetadata = (valueshow: string) => {
    if (valueshow===undefined)return;
    if (valueshow.toLocaleLowerCase().includes(value.toLocaleLowerCase())) 
      return <S.Tag style={{marginLeft: '0px'}} color={"#272727"}> {valueshow} </S.Tag>;
    else return ` ${valueshow}`;
  }

  const renderResultItem = (branImage: any, idx: number) => {
    return <S.Option key={`op${idx}`} onClick={(e)=> {handleSelectItemSearch(branImage)}}>
            {formatMetadata(branImage.type)}
            {formatMetadata(branImage.theme)}

            <S.Tag color={''}>{` ${branImage.imageType.slice(0, -1).toLocaleLowerCase()}`}</S.Tag>
            {branImage.tag && " - Tag: "}
            {formatMetadata(branImage.tag)}
          </S.Option>
  }

  const getCounterText=()=>{
    if (value!==''){
      if(searchResults && (searchResultscounter?.counter || selectedSearchGroup)) {
        let countResults: number = selectedSearchGroup ?  selectedSearchGroup.counter??0 : searchResultscounter.counter??0;
        let searchText: string = selectedSearchGroup ? `${selectedSearchGroup?.search?.type??'' }, ${selectedSearchGroup?.search?.imageType.slice(0, -1).toLocaleLowerCase()}`: value;
        return `${countResults} Result${countResults>1 ? 's':''} for “${searchText}”`;
      } 
    } 
    return  `Search or add filters`;
  }

  return (
    <S.Root>
      <ClickOutside onClickOutside={()=> setShowMenu(false)} event={"mousedown"}>
      <S.SearchContainer>
        <S.Col style={{width: 243 }}>
          <S.InputSearch 
            id="search"
            placeholder ={`Search Brand Library`}
            value={value}
            onChange={handleChange}
            onFocus={ () => handleFocus() }
            onClick={ () => {} }
            onKeyUp={(e)=>handleKeyUp(e)} 
            autoComplete="off"
            style={{
              "height":`40px`,
              "marginLeft":'2px',
              "fontSize":`14px`
            }}
          />
          {showClear && <S.Icon onClick={(e)=>handleClear()}><CloseIcon/></S.Icon>}
      </S.Col>
      
      <S.SearchList style={{display: showMenu?'block':'none'}}>
          <S.OptionsContaine>
              {(searchList && (value.length>3)&&(!showLoading)) && 
              searchList.map((item: any, idx: number)=> renderResultItem(item, idx))}
              {searchResults && searchResults.length===0 && !showLoading &&
                <S.Option style={{"textTransform": "unset"}}>{"No images found for your search."}</S.Option>
              }
          </S.OptionsContaine>
          {showLoading && <Loading/>}
      </S.SearchList>

      {/*{renderFilter()}*/}
      </S.SearchContainer>    
      </ClickOutside>    
      <S.SearchChipsContainer>
        <S.SearchText> {getCounterText()} </S.SearchText>
      </S.SearchChipsContainer>            
    </S.Root>

  );
}

export default SearchBrandLibrary;